import { MyNaviCollegeList } from '@/components/Campaign/mynavi/MyNaviModels/College/MyNaviCollegeList'
import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import type { FilterConditions } from '@/features/pages/mynavi/useMynaviStateMachine'
import { buildFilterParams } from '@/features/pages/mynavi/utils'
import type { MynaviCollegeSummary, MynaviOpenCampusItem } from '@/schemas/v2/mynavi'
import { useTokenStore } from '@/store/useTokenStore'
import { IconChevronLeft } from '@tabler/icons-react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const PAGE_LIMIT = 10

// ─── Date Formatting ──────────────────────────────────────────────────────

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'] as const

const formatEventDate = (eventDate: string | null): { year: number; date: string } => {
  if (!eventDate) return { year: new Date().getFullYear(), date: '日程未定' }
  const d = new Date(eventDate)
  return {
    year: d.getFullYear(),
    date: `${d.getMonth() + 1}/${d.getDate()}(${WEEKDAYS[d.getDay()]})`,
  }
}

const toCollegeEvent = (oc: MynaviOpenCampusItem, wishedMap: Record<number, boolean>) => {
  const { year, date } = formatEventDate(oc.eventDate)
  return {
    eventId: oc.id,
    year,
    date,
    campus: oc.location ?? '',
    isWanted: wishedMap[oc.id] ?? oc.isWished,
    stampId: null, // 検索結果一覧では stampId は提供されないため null（OC 参加ボタンは非活性）
  }
}

// ─── Types ────────────────────────────────────────────────────────────────

type SearchResultProps = {
  /** XState context から渡されるフィルター条件 */
  filterConditions: FilterConditions
  /** 学校名テキスト検索クエリ（空文字はフィルター検索を使用） */
  searchQuery: string
  /** 学校詳細画面へ遷移するコールバック（XState NAVIGATE_COLLEGE_DETAIL を発火する） */
  onCollegeDetailOpen: (collegeId: number) => void
  /** 前の画面に戻るコールバック（XState NAVIGATE_BACK を発火する） */
  onBack: () => void
}

// ─── Component ────────────────────────────────────────────────────────────

export const SearchResult = ({ filterConditions, searchQuery, onCollegeDetailOpen, onBack }: SearchResultProps) => {
  const api = useApi()
  const { loginUrl } = useCampaignSlug()
  const router = useRouter()
  const idToken = useTokenStore((s) => s.idToken)

  // 認証あり/なしで適切なエンドポイントを選択（早期リターンで型キャスト不要）
  const fetchPage = useCallback(
    async (page: number): Promise<{ total: number; colleges: MynaviCollegeSummary[] }> => {
      const base = { page, limit: PAGE_LIMIT, excludePast: true } as const
      if (searchQuery) {
        const fn = idToken ? api.auth.fetchMynaviCollegesSearch : api.guest.fetchMynaviCollegesSearch
        return fn({ q: searchQuery, ...base })
      }
      const fn = idToken ? api.auth.fetchMynaviCollegesFilter : api.guest.fetchMynaviCollegesFilter
      return fn({
        ...buildFilterParams(filterConditions),
        ...base,
      })
    },
    [api, idToken, searchQuery, filterConditions],
  )

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['mynavi', 'colleges', !!idToken, searchQuery, filterConditions],
    queryFn: ({ pageParam = 1 }) => fetchPage(pageParam as number),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.flatMap((p) => p.colleges).length
      return loaded < lastPage.total ? allPages.length + 1 : undefined
    },
    initialPageParam: 1,
  })

  // collegeType ラベル変換用マップ（value → label）
  const { data: collegeTypesData } = useQuery({
    queryKey: ['mynavi', 'college-types'],
    queryFn: () => api.guest.fetchMynaviCollegeTypes(),
    staleTime: 300_000,
  })
  const collegeTypeMap = useMemo(
    () => new Map(collegeTypesData?.collegeTypes.map((t) => [t.value, t.label]) ?? []),
    [collegeTypesData],
  )

  const colleges = useMemo(() => data?.pages.flatMap((p) => p.colleges) ?? [], [data])
  const total = data?.pages[0]?.total ?? 0

  // ─── ブラウザバック対応 ───────────────────────────────────────────────────
  // マウント時にダミー history エントリを積む。
  // ブラウザバック（popstate）を検知して XState の NAVIGATE_BACK に変換する。
  // ref を使うことで useEffect の依存配列を空にし、ダミーエントリの多重追加を防ぐ。

  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  useEffect(() => {
    // state マーカーを付与してダミー履歴を積む。
    // cleanup 時にマーカーが現在の state に残っている場合のみ back() を呼ぶことで、
    // router.push 等で unmount された場合の遷移打ち消しを防ぐ。
    const MARKER = { mynaviSearch: true }
    let popstateFired = false
    window.history.pushState(MARKER, '', window.location.href)
    const handlePopState = () => {
      popstateFired = true
      onBackRef.current()
    }
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
      // UIの「戻る」ボタン経由かつダミー履歴が現在の state に残っている場合のみ解消する
      if (!popstateFired && (window.history.state as Record<string, unknown>)?.mynaviSearch) {
        window.history.back()
      }
    }
  }, [])

  // ─── Infinite Scroll ────────────────────────────────────────────────────

  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = bottomRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // ─── 「行きたい」楽観的更新 ────────────────────────────────────────────

  // wishedMap はユーザー操作による上書きのみを保持する。
  // 初期表示は toCollegeEvent の `wishedMap[oc.id] ?? oc.isWished` で API値にフォールバックする。
  const [wishedMap, setWishedMap] = useState<Record<number, boolean>>({})

  const handleToggleWanted = useCallback(
    async (ocId: number) => {
      if (!idToken) {
        router.push(loginUrl)
        return
      }
      // 現在の有効値（ユーザー操作優先、なければAPIの初期値）を updater 外で決定し
      // 並行レンダリングでも安定した値を API 呼び出しに渡せるようにする
      const currentIsWished =
        wishedMap[ocId] ?? colleges.flatMap((c) => c.openCampuses).find((oc) => oc.id === ocId)?.isWished ?? false
      const next = !currentIsWished
      setWishedMap((prev) => ({ ...prev, [ocId]: next }))
      try {
        await api.auth.postMynaviWishToVisit(ocId, next)
      } catch {
        // API失敗時はロールバック
        setWishedMap((prev) => ({ ...prev, [ocId]: !next }))
      }
    },
    [api, idToken, loginUrl, router, wishedMap, colleges],
  )

  // ─── JSX ────────────────────────────────────────────────────────────────

  return (
    <main className="bg-[#f5f5f5] min-h-screen">
      {/* 戻るバー */}
      <div className="bg-white flex items-center h-[42px] px-2">
        <button type="button" className="flex items-center gap-1" onClick={onBack}>
          <IconChevronLeft size={24} color="#000" stroke={1.5} />
          <span style={{ fontFamily: "'BIZ UDPGothic', sans-serif" }} className="text-[14px] text-black leading-none">
            戻る
          </span>
        </button>
      </div>

      {/* 検索結果バー */}
      <div className="bg-white flex items-center justify-between px-4 h-[77px]">
        <div className="flex items-baseline gap-1">
          <span
            style={{ fontFamily: "'BIZ UDPGothic', sans-serif" }}
            className="text-[20px] text-[#686868] leading-none"
          >
            検索結果
          </span>
          <span
            style={{ fontFamily: "'BIZ UDPGothic', sans-serif" }}
            className="text-[32px] text-[#058bbe] leading-none"
          >
            {isLoading ? '-' : total}
          </span>
          <span
            style={{ fontFamily: "'BIZ UDPGothic', sans-serif" }}
            className="text-[20px] text-[#686868] leading-none"
          >
            校
          </span>
        </div>
        <button
          type="button"
          className="h-[38px] w-[96px] border border-[#058bbe] rounded text-[#686868] text-[16px] bg-white"
          style={{ fontFamily: "'BIZ UDPGothic', sans-serif" }}
          onClick={() => onBack()}
        >
          条件変更
        </button>
      </div>

      {/* ローディング */}
      {isLoading && <div className="py-8 text-center text-[#686868]">読み込み中...</div>}

      {/* 検索結果リスト */}
      <div className="flex flex-col">
        {colleges.map((college) => (
          <MyNaviCollegeList
            key={college.id}
            collegeName={college.name}
            collegeType={collegeTypeMap.get(college.collegeType ?? '') ?? college.collegeType ?? ''}
            prefecture={college.prefecture ?? ''}
            imageUrl={college.imageUrl ?? undefined}
            events={college.openCampuses.slice(0, 2).map((oc) => toCollegeEvent(oc, wishedMap))}
            totalEvents={college.openCampuses.length}
            onBrochureRequest={
              college.brochureUrl ? () => window.open(college.brochureUrl!, '_blank', 'noopener,noreferrer') : undefined
            }
            onEventInfo={
              college.openCampusUrl
                ? () => window.open(college.openCampusUrl!, '_blank', 'noopener,noreferrer')
                : undefined
            }
            onMoreSchedule={() => onCollegeDetailOpen(college.id)}
            onOcParticipateRequest={() => {
              // 検索結果一覧では stampId が null のため OC 参加ボタンは常に非活性（MyNaviCollegeList 側で制御）
            }}
            onToggleWanted={(eventId) => handleToggleWanted(eventId)}
          />
        ))}
      </div>

      {/* 次ページ読み込みトリガー（IntersectionObserver 用） */}
      <div ref={bottomRef} className="h-8" />

      {/* 追加読み込み中インジケーター */}
      {isFetchingNextPage && <div className="py-4 text-center text-[#686868] text-sm">読み込み中...</div>}
    </main>
  )
}
